import { ErrorTypes } from '@fabric-ui/components';
import { AuthStorageKeys } from '@utils/localStorageAuthUtils.utils';
import { Amplify } from 'aws-amplify';
import * as Auth from 'aws-amplify/auth';
import { JWT } from 'aws-amplify/auth';
import { gql } from 'graphql-request';
import { AuthenticationProvider, AuthPlatform, SessionInfo, TokenUser, UserInfo } from './auth.types';
import { ZidAuthUrlParams } from './zid-oidc/tokenUtils';

export const COGNITO_ERROR_PREFIX = '[Cognito Error]';

const configureAuthProvider = (): void => {
  const { connectionDetails } = JSON.parse(localStorage.getItem(AuthStorageKeys.authConfig) || '{}');

  if (connectionDetails) {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolClientId: connectionDetails.userPoolWebClientId,
          userPoolId: connectionDetails.userPoolId,
          loginWith: {
            oauth: {
              domain: connectionDetails.userPoolDomain,
              scopes: ['email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],
              redirectSignIn: [`${window.location.origin}`],
              redirectSignOut: [`${window.location.origin}`],
              responseType: ZidAuthUrlParams.code,
            },
          },
        },
      },
    });
  }
};

const fetchSession = async ({
  forceRefresh = true,
  firstLogin = false,
}: {
  forceRefresh?: boolean;
  firstLogin?: boolean;
}): Promise<SessionInfo> => {
  try {
    const res = await Auth.fetchAuthSession({ forceRefresh });
    if (!res.tokens?.idToken && !firstLogin) {
      console.error(
        `${ErrorTypes.Authentication} ${COGNITO_ERROR_PREFIX}: token is empty after refresh. response: ${JSON.stringify(res)}, forceRefresh: ${forceRefresh}`
      );
    }

    const accountToken = res.tokens?.idToken;
    const defaultAccountId: string | undefined = accountToken?.payload.default_account_id?.toString();
    const userInfo = accountToken ? extractUserPropertiesFromToken(accountToken?.payload as TokenUser) : undefined;

    return { accountToken, userInfo, defaultAccountId };
  } catch (error) {
    console.error(
      `${ErrorTypes.Authentication} ${COGNITO_ERROR_PREFIX}: Fetch session error: ${(error as any).message} ${(error as any).underlyingError} `
    );
    return { error: (error as any).underlyingError };
  }
};

const signOut = async () => Auth.signOut();

export const clearLS = () => {
  const cognitoLSValues = Object.keys(localStorage).filter(key => /^CognitoIdentityServiceProvider(.*)$/.test(key));
  cognitoLSValues.forEach(key => localStorage.removeItem(key));
};

const overrideAccountId = async (accountId: string, attempts = 0): Promise<void> => {
  try {
    await Auth.updateUserAttributes({
      userAttributes: {
        'custom:web_account_id': accountId,
      },
    });
  } catch (error) {
    if (attempts < 1) {
      await overrideAccountId(accountId, attempts + 1);
    } else {
      console.error(
        `${ErrorTypes.Authentication} ${COGNITO_ERROR_PREFIX}: Error changing account ID user attribute to: ${accountId} after 2 attempts ${JSON.stringify(
          (error as any).message || error
        )}`
      );
    }
  }
};

const signIn = async (state?: string): Promise<void> => {
  try {
    const authConfig = JSON.parse(localStorage.getItem(AuthStorageKeys.authConfig) || '{}');
    const signInDetails: { customState: string; provider?: { custom: string } } = {
      customState: encodeURIComponent(state || window.location.pathname + window.location.search),
    };

    const providerName = authConfig.connectionDetails?.userPoolIdentityProviderName || authConfig.userPoolIdentityProviderName;
    if (providerName) {
      signInDetails.provider = { custom: providerName };
    }

    await Auth.signInWithRedirect(signInDetails);
  } catch (e) {
    console.error(`${ErrorTypes.Authentication} ${COGNITO_ERROR_PREFIX}: ${e}`);
  }
};

export const extractUserPropertiesFromToken = (tokenPayload: TokenUser): UserInfo => ({
  email: tokenPayload.email as unknown as string,
  userId: tokenPayload.avalor_user_id as unknown as string,
  roleId: tokenPayload.user_role_id as unknown as string,
  accountId: tokenPayload.account_id as unknown as string,
  abac: tokenPayload.abac_filters as unknown as string,
  permissions: tokenPayload.scope as unknown as string,
  userName: tokenPayload.avalor_user_name as unknown as string,
  roleName: tokenPayload.role_name as unknown as string,
});

const formatLocalSessionObject = ({ jwt }) => extractUserPropertiesFromToken(jwt);

const processAuthRedirect = () => {};

const getUser = async () => {
  await Auth.getCurrentUser();
};

const getAccountFromToken = (decodedToken: JWT): string => decodedToken.payload!.account_id as string;

export const getAdditionalUserData = gql`
  query getUserAdditionalData {
    getUserAdditionalData {
      accounts {
        accountId
        accountName
        userRoleId
      }
      favoriteApps
    }
  }
`;

export const cognitoAuthenticationProvider: AuthenticationProvider = {
  configureAuthProvider,
  fetchSession,
  signOut,
  overrideAccountId,
  signIn,
  clearLS,
  isUserAuthenticated: getUser,
  processAuthRedirect,
  getAccountFromToken,
  formatLocalSessionObject,
  getAdditionalUserData,
  provider: AuthPlatform.COGNITO,
};
